'use client';

import {MeinText} from '@components/@typography/MeinText';
import {
    useVideoPlayer,
    useVideoPlayerState,
} from '@components/@ui/VideoPlayer/useVideoPlayer';
import {IChapterCue} from '@utils/parseChapterMarks';
import {AnimatePresence, motion} from 'framer-motion';
import {FC, useEffect, useMemo, useRef} from 'react';
import {createPortal} from 'react-dom';
import {useChaptersStore} from '../@hooks/useChaptersStore';
import styles from './ChaptersTitle.module.css';

interface Props {
    cues: IChapterCue[];
}

const ChaptersTitle: FC<Props> = ({cues}) => {
    const {_injectIntoVideoJsUI} = useVideoPlayer();
    const isReady = useVideoPlayerState(s => s.isReady);
    const activeChapterTimestamp = useChaptersStore(
        s => s.activeChapterTimestamp,
    );

    const containerRef = useRef<HTMLDivElement | null>(null);

    const activeChapter = useMemo(() => {
        if (!cues || !cues.length) {
            return null;
        }

        return cues.find(
            c =>
                c.startTime <= activeChapterTimestamp &&
                c.endTime > activeChapterTimestamp,
        );
    }, [activeChapterTimestamp, cues]);

    useEffect(() => {
        if (typeof document === 'undefined' || !isReady) {
            return;
        }

        const el = document.createElement('div');
        if (containerRef.current) {
            containerRef.current.remove();
        }

        containerRef.current = el;
        _injectIntoVideoJsUI('.vjs-control-bar', el);

        return () => {
            el.remove();
            containerRef.current = null;
        };
    }, [isReady]);

    return containerRef.current
        ? createPortal(
              <AnimatePresence mode="wait">
                  {activeChapter && (
                      <motion.div
                          key={activeChapter.title}
                          className={styles.chapter}
                          initial={{opacity: 0, y: 2}}
                          animate={{opacity: 1, y: 0}}
                          exit={{opacity: 0, y: -2}}
                          transition={{duration: 0.2}}
                      >
                          <div className={styles.separator} />
                          <MeinText size="s" className={styles.chapterTitle}>
                              {activeChapter.title}
                          </MeinText>
                      </motion.div>
                  )}
              </AnimatePresence>,
              containerRef.current,
          )
        : null;
};

export default ChaptersTitle;
